import { UserRoles } from '@dynamika-shared/types/auth';

export const RETURN_TO_KEY = 'd7_post_login_return_to';

// Higher number = more privileged. Drives both the multi-role pick and the
// no-access fallback. There is exactly one ranking, used everywhere.
export const ROLE_RANK: Record<UserRoles, number> = {
  [UserRoles.ADMIN]: 100,
  [UserRoles.SUPPORT]: 90,
  [UserRoles.PROJECT_MANAGER]: 80,
  [UserRoles.SPONSOR]: 70,
  [UserRoles.DATA_MANAGER]: 60,
  [UserRoles.READER]: 50,
  [UserRoles.IMAGING_SPECIALIST]: 40,
  [UserRoles.VALIDATION_SPECIALIST]: 30,
  [UserRoles.SITE]: 20,
  [UserRoles.EXTERNAL_USER]: 10,
  [UserRoles.USER]: 0,
};

const BACK_OFFICE_ROLES: UserRoles[] = [
  UserRoles.PROJECT_MANAGER,
  UserRoles.ADMIN,
  UserRoles.SUPPORT,
];

// Ordered longest-prefix-first so a broader prefix never shadows a more specific
// one. `/sponsors` (back-office audit trails) MUST precede `/sponsor` (SPONSOR
// area), otherwise `'/sponsors/...'.startsWith('/sponsor')` would mis-match.
// USER and DATA_MANAGER are intentionally absent: their landing areas (profile)
// are not role-exclusive, so a deep link there needs no role to be resolved and
// correctly falls through to the default-dashboard behavior.
const ROUTE_PREFIX_TO_ROLES: ReadonlyArray<{
  prefix: string;
  roles: UserRoles[];
}> = [
  { prefix: '/validation-specialist', roles: [UserRoles.VALIDATION_SPECIALIST] },
  { prefix: '/ext-user', roles: [UserRoles.EXTERNAL_USER] },
  { prefix: '/clinical-trials', roles: BACK_OFFICE_ROLES },
  { prefix: '/users-and-logs', roles: BACK_OFFICE_ROLES },
  { prefix: '/operations', roles: BACK_OFFICE_ROLES },
  { prefix: '/inventory', roles: BACK_OFFICE_ROLES },
  { prefix: '/sponsors', roles: BACK_OFFICE_ROLES },
  { prefix: '/reader', roles: [UserRoles.READER] },
  { prefix: '/sponsor', roles: [UserRoles.SPONSOR] },
  { prefix: '/subject', roles: [UserRoles.SITE] },
  { prefix: '/qc', roles: [UserRoles.IMAGING_SPECIALIST] },
];

// Returns every role that can access `path`, or [] when no area matches.
export const resolveRolesForPath = (path: string): UserRoles[] => {
  const match = ROUTE_PREFIX_TO_ROLES.find(({ prefix }) =>
    path.startsWith(prefix)
  );

  return match?.roles ?? [];
};

const byRankDesc = (a: UserRoles, b: UserRoles) => ROLE_RANK[b] - ROLE_RANK[a];

export type PostLoginTarget = { role: UserRoles; path: string };

// Given the user's roles and a returnTo, decide the role to activate and where
// to go. Returns null when the user has no qualifying role (caller falls back).
export const resolvePostLoginTarget = (
  returnTo: string,
  userRoles: UserRoles[]
): PostLoginTarget | null => {
  const qualifying = resolveRolesForPath(returnTo)
    .filter(role => userRoles.includes(role))
    .sort(byRankDesc);

  if (qualifying.length === 0) {
    return null;
  }

  return { role: qualifying[0], path: returnTo };
};
